"use client"

import { useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { ArrowRight } from "lucide-react"
import DonistaLogo from "@/components/icons/donista-logo"

const strings = {
  de: {
    title: "Seite nicht gefunden",
    message: "Die angeforderte Seite existiert nicht oder wurde verschoben.",
    goHome: "Zur Startseite",
  },
  en: {
    title: "Page not found",
    message: "The page you're looking for doesn't exist or has been moved.",
    goHome: "Go to Home",
  },
} as const

function detectLocale(): "de" | "en" {
  if (typeof window === "undefined") return "de"
  const path = window.location.pathname
  return path.startsWith("/en/") || path === "/en" ? "en" : "de"
}

export default function NotFound() {
  const [locale] = useState(detectLocale)
  const t = strings[locale]
  const homePath = locale === "en" ? "/en" : "/"

  return (
    <>
      <style>{`
        @keyframes errorFadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes errorFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes logoPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(0.92); }
        }
        .error-fade-up {
          opacity: 0;
          animation: errorFadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .error-float {
          animation: errorFloat 6s ease-in-out infinite;
        }
      `}</style>
      <div
        className="fixed inset-0 flex flex-col items-center justify-center px-4 z-50"
        style={{
          backgroundImage: "url('/background/ffflux.svg')",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      >
        <div className="error-fade-up">
          <DonistaLogo className="h-10 w-10 mx-auto mb-14" style={{ animation: "logoPulse 4s ease-in-out infinite" }} />
        </div>

        <div className="flex items-baseline gap-1 md:gap-2 select-none error-float" style={{ animationDelay: "0.8s" }}>
          <span
            className="text-[11rem] md:text-[14rem] font-black text-primary/8 leading-none error-fade-up"
            style={{ animationDelay: "0.1s" }}
          >
            4
          </span>
          <span
            className="text-[11rem] md:text-[14rem] font-black text-primary/8 leading-none error-fade-up"
            style={{ animationDelay: "0.2s" }}
          >
            0
          </span>
          <span
            className="text-[11rem] md:text-[14rem] font-black text-primary/8 leading-none error-fade-up"
            style={{ animationDelay: "0.3s" }}
          >
            4
          </span>
        </div>

        <h1
          className="text-3xl md:text-4xl font-bold mt-2 mb-3 text-center error-fade-up"
          style={{ animationDelay: "0.4s" }}
        >
          {t.title}
        </h1>
        <p
          className="text-lg text-muted-foreground max-w-md text-center mb-12 error-fade-up"
          style={{ animationDelay: "0.5s" }}
        >
          {t.message}
        </p>

        <div className="error-fade-up" style={{ animationDelay: "0.6s" }}>
          <Button asChild size="lg" className="group px-8">
            <Link href={homePath}>
              {t.goHome}
              <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </>
  )
}
